import { apiGet, APIURL, cancelSelectEvent, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseTopMovers {
  private container: HTMLElement;
  private config: WidgetConfig & { mover_type?: string; interval?: string };
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private lastFetchedAt: Date | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  } 

  private getDefaultConfig(): Partial<WidgetConfig> & { mover_type: string; interval: string } {
    return {
      headerPinned: true,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      mover_type: 'MostActive',
      interval: '1d'
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox traderverse-widget ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Top Movers</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <tooltip-wrapper id="lastUpdatedTooltip"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> </svg><tooltip><span>Last Updated</span><strong>N/A</strong></tooltip></tooltip-wrapper>
            <i class="pi pi-ellipsis-v"></i>
          </div>
        </div>
        <div class="inBody">

          <div class="optionsRow" data-testing-id="options-row">
            <div class="item" data-testing-id="mover-type-selector">
              <select class="mover-type-dropdown" data-testing-id="mover-type-dropdown">
                <option value="MostActive">Most Active</option>
                <option value="Gainer">Gainer</option>
                <option value="looser">Loser</option>
              </select>
            </div>
            <span></span>
          </div>
          
          <div class="chartDv" id="topMoversContainer" data-testing-id="top-movers-container"></div>
          
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const moverTypeSelect = this.shadowRoot.querySelector('.mover-type-dropdown') as HTMLSelectElement;
    const intervalSelect = this.shadowRoot.querySelector('.interval-dropdown') as HTMLSelectElement;

    moverTypeSelect?.addEventListener('change', () => {
      this.config.mover_type = moverTypeSelect.value;
      this.setupChart();
    });

    intervalSelect?.addEventListener('change', () => {
      this.config.interval = intervalSelect.value;
      this.setupChart();
    });

    if (this.config.mover_type && moverTypeSelect) moverTypeSelect.value = this.config.mover_type;
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(): Promise<void> {
    try {
      const { mover_type, interval } = this.config;

      const response = await apiGet({
        method: "POST",
        url: `${APIURL}/v3/discover/widget_movers`,
        body: {
          mover_type: mover_type || "MostActive",
          interval: interval || "1d",
        },
      });

      if (!response.ok) throw new Error(`API Error: ${response.status}`);

      const result = await response.json();

      const key = mover_type || "MostActive";
      if (result?.data && Array.isArray(result.data[key])) {
        this.currentData = result.data[key];
      } else {
        console.warn("Unexpected API response format:", result);
        this.currentData = [];
      }
      const fromApi = (result as any)?.last_updated_at ?? (Array.isArray(this.currentData) ? (this.currentData[0] as any)?.last_updated_at : null);
      this.lastFetchedAt = fromApi ? new Date(fromApi) : new Date();

      this.updateDisplay();
      this.emit("dataUpdate", this.currentData);

    } catch (err) {
      console.error("Error fetching top movers data:", err);
    }
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }

  private updateLastUpdatedTooltip(): void {
    const lastUpdated = this.lastFetchedAt ? this.formatLastUpdated(this.lastFetchedAt) : 'N/A';
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
  }

  private updateDisplay(): void {
    this.updateLastUpdatedTooltip();
    const container = this.shadowRoot.querySelector('#topMoversContainer');
    const chartDiv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;

    if (!container) return;

    if (!this.currentData || !Array.isArray(this.currentData) || this.currentData.length === 0) {
      container.innerHTML = getNoDataHTML();
      return;

    }
    chartDiv?.removeAttribute('data-testing-id');

    if (chartDiv) {
      chartDiv.setAttribute('data-testing-id', 'Top Movers');
    }


    const headerHtml = `
    <div class="topMoversMain" data-testing-id="top-movers-main">
      <div class="item header" data-testing-id="table-header">
        <p data-testing-id="header-symbol">Symbol</p>
        <p data-testing-id="header-change">Change</p>
        <p data-testing-id="header-chart">Chart</p>
        <p data-testing-id="header-price">Price</p>
      </div>
    `;
    
    let rowsHtml = '';
    for (let idx = 0; idx < this.currentData.length; idx++) {
      const item = this.currentData[idx];
      const logoUrl = `https://storage.googleapis.com/analytics-images-traderverse/stocks_v1/${item.symbol}.svg`;
      const changeClass = item.pct_change >= 0 ? 'up' : 'down';
      const changeValue = (typeof item.pct_change === 'number' ? item.pct_change.toFixed(2) : '0.00');
      const priceValue = item.price ? item.price.toFixed(2) : '0.00';

      rowsHtml += `
      <div class="item body" 
           data-testing-id="top-movers-row-${idx}" 
           data-index="${idx}"
           data-symbol="${item.symbol || ''}" 
           data-change="${item.pct_change || 0}"
           data-change-class="${changeClass}"
           data-price="${item.price || 0}">
        <p data-testing-id="cell-symbol">
          <img src="${logoUrl}" alt="logo" width="20" height="20" data-testing-id="symbol-img"> 
          ${item.symbol}
        </p>
        <p data-testing-id="cell-change">
          <span class="change ${changeClass}" data-testing-id="change-value">
            ${changeValue}%
          </span>
        </p>
        <p data-testing-id="cell-chart">
          <span class="sparkline" id="spark-${idx}" data-testing-id="sparkline-${idx}"></span>
        </p>
        <p data-testing-id="cell-price">$${priceValue}</p>
      </div>
      `;
    }

    container.innerHTML = headerHtml + rowsHtml + `</div>`;
    
    setTimeout(() => {
      this.currentData.forEach((item: any, idx: number) => {
        const el = this.shadowRoot.querySelector(`#spark-${idx}`) as HTMLElement;
        if (typeof item.pct_change !== 'number' || isNaN(item.pct_change)) {
          if (el) {
            el.innerHTML = `<span style="color:#888;font-size:0.9em;" data-testing-id="sparkline-na">N/A</span>`;
          }
          return;
        }
        if (el && (window as any).echarts) {
          const chart = (window as any).echarts.init(el);
          const lineColor = item.pct_change >= 0 ? '#4caf50' : '#f44336';

          const chartData = Array.isArray(item.chart)
            ? item.chart
            : Array.isArray(item.priceChart)
              ? item.priceChart
              : [];
          let xData: string[];
          let yData: number[];

          if (chartData.length > 0) {
            xData = chartData.map((c: any) => {
              const t = c.timestamp;
              if (!t) return '';
              try {
                const d = new Date(t);
                return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
              } catch {
                return String(t);
              }
            });
            yData = chartData.map((c: any) => typeof c.close === 'number' ? c.close : c.price);
          } else {
            xData = ['O', 'L', 'H', 'C'];
            yData = [item.open, item.low, item.high, item.price];
          }

          chart.setOption({
            grid: { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },
            xAxis: { type: 'category', show: false, data: xData, boundaryGap: false },
            yAxis: { type: 'value', show: false, scale: true },
            series: [{
              type: 'line',
              data: yData,
              smooth: true,
              lineStyle: { width: 1, color: lineColor },
              symbol: 'none',
              areaStyle: { opacity: 0.1, color: lineColor }
            }],
            tooltip: { show: false }
          });
        }
      });
    }, 100);
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseTopMovers: typeof TraderverseTopMovers; } }
if (typeof window !== 'undefined') { window.TraderverseTopMovers = TraderverseTopMovers; }